import React from 'react';
import type { ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { UserProvider } from './context/UserContext';
import LoginPage from './pages/LoginPage';
import Layout from './components/Layout';
import DashboardPage from './pages/DashboardPage';
import AssetListPage from './pages/AssetListPage';
import AssetCategoryPage from './pages/AssetCategoryPage';
import EmployeePage from './pages/EmployeePage';
import MaintenanceTypePage from './pages/MaintenanceTypePage';
import MaintenanceCampaignPage from './pages/MaintenanceCampaignPage';
import MaintenanceCampaignDetailPage from './pages/MaintenanceCampaignDetailPage';
import CreateMaintenanceCampaignPage from './pages/CreateMaintenanceCampaignPage';
import CompanyPage from './pages/CompanyPage';
import SupplierPage from './pages/SupplierPage';
import UserPage from './pages/UserPage';
import LocationPage from './pages/LocationPage';
import PublicAssetDetail from './pages/PublicAssetDetail';
import EmployeeAssetSearch from './pages/EmployeeAssetSearch';
import InventoryAuditListPage from './pages/InventoryAuditListPage';
import InventoryAuditDetailPage from './pages/InventoryAuditDetailPage';
import SystemAuditLogPage from './pages/SystemAuditLogPage';
import AssetTransferPage from './pages/AssetTransferPage';
import AssetTransferCreatePage from './pages/AssetTransferCreatePage';
import AssetTransferDetailPage from './pages/AssetTransferDetailPage';
import DisposalPage from './pages/DisposalPage';
import DisposalCreatePage from './pages/DisposalCreatePage';
import DisposalDetailPage from './pages/DisposalDetailPage';
import { Spin } from 'antd';

const PrivateRoute = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh'}}><Spin size="large" /></div>;
  return user ? <>{children}</> : <Navigate to="/login" />;
};

const SuperAdminRoute = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'super_admin') return <Navigate to="/" />;
  return <>{children}</>;
};

function App() {
  return (
    <AuthProvider>
      <UserProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/public/asset/:public_uuid" element={<PublicAssetDetail />} />
            <Route path="/employee-search" element={<EmployeeAssetSearch />} />
            <Route path="*" element={
              <Layout>
                <Routes>
                  <Route path="/" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
                  <Route path="/assets" element={<PrivateRoute><AssetListPage /></PrivateRoute>} />
                  <Route path="/asset-categories" element={<PrivateRoute><AssetCategoryPage /></PrivateRoute>} />
                  <Route path="/employees" element={<PrivateRoute><EmployeePage /></PrivateRoute>} />
                  <Route path="/maintenance-types" element={<PrivateRoute><MaintenanceTypePage /></PrivateRoute>} />
                  <Route path="/maintenance-campaigns" element={<PrivateRoute><MaintenanceCampaignPage /></PrivateRoute>} />
                  <Route path="/maintenance-campaigns/:id" element={<PrivateRoute><MaintenanceCampaignDetailPage /></PrivateRoute>} />
                  <Route path="/maintenance-campaigns/create" element={<PrivateRoute><CreateMaintenanceCampaignPage /></PrivateRoute>} />
                  <Route path="/companies" element={<PrivateRoute><SuperAdminRoute><CompanyPage /></SuperAdminRoute></PrivateRoute>} />
                  <Route path="/suppliers" element={<PrivateRoute><SupplierPage /></PrivateRoute>} />
                  <Route path="/users" element={<PrivateRoute><SuperAdminRoute><UserPage /></SuperAdminRoute></PrivateRoute>} />
                  <Route path="/locations" element={<PrivateRoute><LocationPage /></PrivateRoute>} />
                  <Route path="/inventory-audit" element={<PrivateRoute><InventoryAuditListPage /></PrivateRoute>} />
                  <Route path="/inventory-audit/:id" element={<PrivateRoute><InventoryAuditDetailPage /></PrivateRoute>} />
                  <Route path="/asset-transfers" element={<PrivateRoute><AssetTransferPage /></PrivateRoute>} />
                  <Route path="/asset-transfers/create" element={<PrivateRoute><AssetTransferCreatePage /></PrivateRoute>} />
                  <Route path="/asset-transfers/:id" element={<PrivateRoute><AssetTransferDetailPage /></PrivateRoute>} />
                  <Route path="/disposal" element={<PrivateRoute><DisposalPage /></PrivateRoute>} />
                  <Route path="/disposal/create" element={<PrivateRoute><DisposalCreatePage /></PrivateRoute>} />
                  <Route path="/disposal-requests/:id" element={<PrivateRoute><DisposalDetailPage /></PrivateRoute>} />
                  <Route path="/system-audit" element={<PrivateRoute><SuperAdminRoute><SystemAuditLogPage /></SuperAdminRoute></PrivateRoute>} />
                </Routes>
              </Layout>
            } />
          </Routes>
        </Router>
      </UserProvider>
    </AuthProvider>
  );
}

export default App;
